'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export function PageViewTracker({ hasConsent }: { hasConsent: boolean }) {

  const pathname = usePathname()
  const prevPathname = useRef<string | null>(null)
  const isGtagLoaded = useRef(false)

  // This effect handles loading the GA script and tracking page views
  useEffect(() => {

    // Only proceed if consent is given
    if (hasConsent) {

      // If gtag hasn't been loaded, load it and then track the initial page view
      if (!isGtagLoaded.current) {

        console.log("Consent granted. Dynamically loading GA script.")

        // Dynamically create and append the gtag.js script
        const script1 = document.createElement('script')
        script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
        script1.async = true

        // Use the onload event to ensure the gtag function is available
        script1.onload = () => {

          // Dynamically create and append the script to initialize gtag
          const script2 = document.createElement('script')
          script2.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GA_MEASUREMENT_ID}', { anonymize_ip: true });
          `
          document.head.appendChild(script2)

          // Now that gtag is loaded, track the current page view immediately
          if (pathname && window.gtag) {
            window.gtag('config', GA_MEASUREMENT_ID!, {
              page_path: pathname,
            })
            console.log(`Tracked initial page view: ${pathname}`)
            prevPathname.current = pathname
          }
        }
        
        // Add the script to the document head
        document.head.appendChild(script1)

        // Mark gtag as having been loaded
        isGtagLoaded.current = true
      }

      // If the gtag hasn't been loaded, track page views
      else if (pathname && prevPathname.current !== pathname && window.gtag) {
        window.gtag('config', GA_MEASUREMENT_ID!, {
          page_path: pathname,
        })
        console.log(`Tracked page view: ${pathname}`)
        prevPathname.current = pathname
      }
    }
  }, [hasConsent, pathname])

  return null
}
